import * as React from 'react';
import localeService from 'src/dataServices/LocaleService';
import { IRegulatoryBody } from 'src/state/profile/types';
import { fetchRegulatoryBodiesForCountry } from 'src/components/profilev2/ProfileSharedFunctions';
import { getLegalIdentifierValidator } from 'src/components/profilev2/ProfileV2SharedComponents';
import { ORG_TYPE_LIBRARY } from 'src/common/Constants';
import { IRegulatoryBodyExt } from 'src/components/profilev2/RegistrationIdentifier';

/**
 * Custom hook to fetch and enrich regulatory bodies for a given country code or organization type.
 * @param countryCode The country code to fetch regulatory bodies for
 * @param organizationType Optional organization type (e.g., LIBRARY). Takes precedence over countryCode for config lookup
 * @returns Map of enriched regulatory bodies with labels, tooltips, and validators
 */
export const useRegistrationIdentifiers = (
  countryCode: string,
  organizationType?: string
): Map<string, IRegulatoryBodyExt> | undefined => {
  const [extRegulatoryBodies, setExtRegulatoryBodies] = React.useState<Map<string, IRegulatoryBodyExt> | undefined>();

  React.useEffect(() => {
    let mounted = true;

    const loadRegulatoryBodies = async () => {
      try {
        const regConfigId = organizationType === ORG_TYPE_LIBRARY ? ORG_TYPE_LIBRARY.toUpperCase() : countryCode;
        const regulatoryBodies = await fetchRegulatoryBodiesForCountry(regConfigId);

        const extObjs: Map<string, IRegulatoryBodyExt> = new Map<string, IRegulatoryBodyExt>();
        const localeContent = localeService.getCategoryContent('RegulatoryBodies').identifiers[regConfigId] || [];

        (regulatoryBodies || []).forEach((regulatoryBody: IRegulatoryBody) => {
          const extObj = Object.assign({}, regulatoryBody) as IRegulatoryBodyExt;
          extObj.label = localeContent[extObj.textKey]?.['label'];
          extObj.tooltipText = localeContent[extObj.textKey]?.['tooltipText'];
          extObj.validator = getLegalIdentifierValidator(
            regConfigId,
            regulatoryBody.textKey,
            regulatoryBody.regexPattern,
            localeContent[extObj.textKey]?.['label']
          );
          extObjs.set(extObj.textKey, extObj);
        });

        if (mounted) {
          setExtRegulatoryBodies(extObjs);
        }
      } catch (error) {
        console.error('Failed to load registration identifiers:', error);
        if (mounted) {
          setExtRegulatoryBodies(new Map());
        }
      }
    };

    loadRegulatoryBodies();

    return () => {
      mounted = false;
    };
  }, [countryCode, organizationType]);

  return extRegulatoryBodies;
};
